import { useEffect, useState } from "react";
import { api } from "../api";
import {
  updateSettings,
  type AiProviderMode,
  type AiTask,
  type ResponseStyle,
} from "../lib/settings";
import { useSettings } from "../lib/useSettings";
import { formatDate } from "../lib/formatDate";
import type { ByoProvider } from "../types";

// BYOK provider metadata — help text, key-format hint, and the
// recommended model list per provider. The "custom model" free-text
// input accepts anything; these are just the curated picks.
interface ProviderInfo {
  label: string;
  help: string;
  keyPlaceholder: string;
  models: Array<{ id: string; label: string }>;
}

const PROVIDER_INFO: Record<ByoProvider, ProviderInfo> = {
  anthropic: {
    label: "Anthropic",
    help: "Get your key at console.anthropic.com",
    keyPlaceholder: "sk-ant-api03-…",
    models: [
      {
        id: "claude-sonnet-4-6",
        label: "claude-sonnet-4.6 (default, best balance)",
      },
      {
        id: "claude-opus-4-8",
        label: "claude-opus-4.8 (most capable, slower)",
      },
      {
        id: "claude-haiku-4-5",
        label: "claude-haiku-4.5 (fastest, cheapest)",
      },
    ],
  },
  openai: {
    label: "OpenAI",
    help: "Get your key at platform.openai.com/api-keys",
    keyPlaceholder: "sk-…",
    models: [
      { id: "gpt-4o", label: "gpt-4o (default, best balance)" },
      { id: "gpt-4o-mini", label: "gpt-4o-mini (faster, cheaper)" },
      { id: "o3-mini", label: "o3-mini (reasoning, slower)" },
    ],
  },
  gemini: {
    label: "Google Gemini",
    help: "Get your key at aistudio.google.com/apikey",
    keyPlaceholder: "AIza…",
    models: [
      { id: "gemini-2.0-flash", label: "gemini-2.0-flash (default, fast)" },
      { id: "gemini-2.5-pro", label: "gemini-2.5-pro (most capable)" },
      { id: "gemini-2.5-flash", label: "gemini-2.5-flash (balanced)" },
    ],
  },
};

const BYOK_PROVIDERS: ByoProvider[] = ["anthropic", "openai", "gemini"];

// Advanced per-task override labels — only rendered inside the collapsed
// Advanced disclosure, where the technical user is expected to understand
// what task names like "ocr_image" or "interrogate_complex" mean.
const TASK_LABELS: Array<{ task: AiTask; label: string }> = [
  { task: "ocr_image", label: "Reading text from images" },
  { task: "ocr_frame", label: "Video frame transcription" },
  { task: "text_extraction_meta", label: "Document metadata extraction" },
  { task: "record_summary", label: "Record summarisation" },
  { task: "case_summary", label: "Case summary generation" },
  { task: "suggested_action", label: "Suggested next action" },
  { task: "draft_letter", label: "Formal letter drafting" },
  { task: "draft_complaint", label: "Regulatory complaint drafting" },
  { task: "interrogate_complex", label: "Complex interrogation" },
  { task: "interrogate_simple", label: "Simple Q&A" },
];

// Consumer-facing language list — matches the spec exactly (seven
// languages). The underlying setting keeps full ISO 639-1 codes so we can
// grow this list without changing the storage format.
const DRAFT_LANGUAGES: Array<{ value: string; label: string }> = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "nl", label: "Dutch" },
];

interface LocalModel {
  name: string;
  sizeGb: number;
  bestFor: string;
  kind: "local";
  pulled: boolean;
  size_bytes: number | null;
}
interface CloudModel {
  name: string;
  kind: "cloud";
  bestFor: string;
  configured: boolean;
}

interface UsageSummary {
  all_time: { n: number; in_tok: number; out_tok: number; cost: number };
  this_month: { n: number; in_tok: number; out_tok: number; cost: number };
  month_projection_usd: number;
  savings_usd: number;
  breakdown: Array<{
    task_type: string;
    provider: string;
    n: number;
    in_tok: number;
    out_tok: number;
    cost: number;
  }>;
}

interface UsageMeter {
  ai_calls: number;
  // Breakdown of ai_calls into generation/retry vs Tier-2 quality-audit calls.
  generation_calls?: number;
  auditor_calls?: number;
  // Null for BYOK users (no CaseThread-side ceiling).
  limit: number | null;
  warn_threshold: number | null;
  reset_at: string;
  byo_key_active: boolean;
  byo_key_set_at: string | null;
  byo_provider: ByoProvider | null;
  byo_model: string | null;
  allowed: boolean;
}

export function ModelLabel({
  name,
  kind,
}: {
  name: string;
  kind: "local" | "cloud";
}) {
  return (
    <span className={`model-label model-${kind}`}>
      <span className="model-label-icon" aria-hidden>
        {kind === "local" ? "🏠" : "☁️"}
      </span>
      <span className="model-label-name">{name}</span>
      <span className="model-label-badge">
        {kind === "local" ? "Local" : "Cloud"}
      </span>
    </span>
  );
}

export default function AiSettings() {
  const settings = useSettings();
  const [models, setModels] = useState<{
    local: LocalModel[];
    cloud: CloudModel[];
    ollama_available: boolean;
  } | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [meter, setMeter] = useState<UsageMeter | null>(null);
  const [pullState, setPullState] = useState<
    Record<string, { status: string; progress: number } | null>
  >({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  // BYO key state — lives in Advanced. The plaintext key is never read
  // back from the server; we only know whether one is set, and for
  // which provider + model.
  const [keyStatus, setKeyStatus] = useState<{
    set: boolean;
    set_at: string | null;
    provider: ByoProvider;
    model: string | null;
  } | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [keyProvider, setKeyProvider] = useState<ByoProvider>("anthropic");
  // Dropdown pick from the recommended list. The "" sentinel means
  // "no selection — use provider default (unless customModel is set)".
  const [modelPick, setModelPick] = useState<string>("");
  const [customModel, setCustomModel] = useState("");
  const [keySaving, setKeySaving] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  async function refreshMeter() {
    try {
      setMeter(await api.aiUsageMeter());
    } catch (err) {
      console.error("usage meter load failed", err);
    }
  }

  async function refreshKeyStatus() {
    try {
      setKeyStatus(await api.getApiKey());
    } catch (err) {
      console.error("api key status load failed", err);
    }
  }

  async function refreshModels() {
    try {
      const data = await api.listModels();
      setModels(data);
      setModelsError(null);
    } catch (err) {
      setModelsError((err as Error).message);
    }
  }

  async function refreshUsage() {
    try {
      setUsage(await api.usage());
    } catch (err) {
      console.error("usage load failed", err);
    }
  }

  useEffect(() => {
    void refreshMeter();
    void refreshKeyStatus();
  }, []);

  // Defer the Advanced-only network calls until the section is opened —
  // the provider list + cost breakdown only matter for technical users
  // poking around, and this keeps the initial settings render light.
  useEffect(() => {
    if (!advancedOpen) return;
    if (!models) void refreshModels();
    if (!usage) void refreshUsage();
  }, [advancedOpen, models, usage]);

  async function pull(name: string) {
    setPullState((p) => ({ ...p, [name]: { status: "starting", progress: 0 } }));
    try {
      await api.pullModel(name, (line) => {
        try {
          const event = JSON.parse(line) as {
            status?: string;
            completed?: number;
            total?: number;
          };
          const pct =
            event.completed && event.total
              ? Math.round((event.completed / event.total) * 100)
              : 0;
          setPullState((p) => ({
            ...p,
            [name]: {
              status: event.status ?? "pulling",
              progress: pct,
            },
          }));
        } catch {
          /* non-JSON heartbeat line — ignore */
        }
      });
      await refreshModels();
    } catch (err) {
      setPullState((p) => ({
        ...p,
        [name]: { status: `error: ${(err as Error).message}`, progress: 0 },
      }));
    } finally {
      window.setTimeout(() => {
        setPullState((p) => ({ ...p, [name]: null }));
      }, 2500);
    }
  }

  function setResponseStyle(v: ResponseStyle) {
    updateSettings({
      interrogate: { ...settings.interrogate, responseStyle: v },
    });
  }

  function setDraftLanguage(v: string) {
    updateSettings({ interrogate: { ...settings.interrogate, language: v } });
  }

  async function setProvider(p: AiProviderMode) {
    updateSettings({ ai: { ...settings.ai, provider: p } });
  }

  async function setPrivacy(v: boolean) {
    updateSettings({ ai: { ...settings.ai, privacy: v } });
  }

  async function setOverride(task: AiTask, value: string) {
    const next = { ...settings.ai.overrides };
    if (!value || value === "auto") delete next[task];
    else next[task] = value;
    updateSettings({ ai: { ...settings.ai, overrides: next } });
  }

  async function saveApiKey() {
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    setKeySaving(true);
    setKeyError(null);
    // Custom model wins over the dropdown pick so a power user can
    // point at a fine-tune or a brand-new model ID without waiting
    // for us to curate it. Empty string → null (use provider default).
    const modelToSave =
      customModel.trim() || modelPick || null;
    try {
      const res = await api.setApiKey({
        key: trimmed,
        provider: keyProvider,
        model: modelToSave,
      });
      setKeyStatus(res);
      setKeyInput("");
      setCustomModel("");
      setModelPick("");
      // BYO status affects the visible usage meter badge — refresh both.
      void refreshMeter();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg === "invalid_api_key_format") {
        const info = PROVIDER_INFO[keyProvider];
        setKeyError(
          `That doesn't look like a ${info.label} API key.`,
        );
      } else if (msg === "key_validation_failed") {
        setKeyError(
          "The provider rejected this key. Double-check you copied it correctly.",
        );
      } else {
        setKeyError(msg);
      }
    } finally {
      setKeySaving(false);
    }
  }

  async function removeApiKey() {
    setKeySaving(true);
    setKeyError(null);
    try {
      await api.deleteApiKey();
      setKeyStatus({
        set: false,
        set_at: null,
        provider: "anthropic",
        model: null,
      });
      void refreshMeter();
    } catch (err) {
      setKeyError((err as Error).message);
    } finally {
      setKeySaving(false);
    }
  }

  const totalLocalGb = (models?.local ?? [])
    .filter((m) => m.pulled)
    .reduce(
      (acc, m) => acc + (m.size_bytes ? m.size_bytes / 1e9 : m.sizeGb),
      0,
    );

  const providerModels: Array<{
    value: string;
    label: string;
    kind: "local" | "cloud";
    disabled?: boolean;
  }> = [
    { value: "auto", label: "Auto (use routing table)", kind: "cloud" },
  ];
  for (const m of models?.local ?? []) {
    providerModels.push({
      value: m.name,
      label: m.name,
      kind: "local",
      disabled: !m.pulled,
    });
  }
  for (const m of models?.cloud ?? []) {
    providerModels.push({ value: m.name, label: m.name, kind: "cloud" });
  }

  return (
    <>
      {/* ---------- AI responses ---------- */}
      <section className="settings-section">
        <h3>AI responses</h3>
        <Row
          label="Response style"
          hint="Controls how much explanation the AI includes in its answers"
        >
          <div className="segmented">
            {(
              [
                ["concise", "Concise"],
                ["detailed", "Detailed"],
              ] as [ResponseStyle, string][]
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={
                  settings.interrogate.responseStyle === v ? "active" : ""
                }
                onClick={() => setResponseStyle(v)}
              >
                {label}
              </button>
            ))}
          </div>
        </Row>
      </section>

      {/* ---------- Draft language ---------- */}
      <section className="settings-section">
        <h3>Draft language</h3>
        <Row
          label="Language"
          hint="Language used when drafting letters and complaints"
        >
          <select
            value={normaliseLanguage(settings.interrogate.language)}
            onChange={(e) => setDraftLanguage(e.target.value)}
          >
            {DRAFT_LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Row>
      </section>

      {/* ---------- Usage meter ---------- */}
      <section className="settings-section">
        <h3>AI requests this month</h3>
        <UsageMeterPanel meter={meter} />
      </section>

      {/* ---------- Powered by ---------- */}
      <section className="settings-section">
        <h3>Powered by</h3>
        <p className="muted">
          AI features are powered by Claude, made by{" "}
          <a
            href="https://anthropic.com"
            target="_blank"
            rel="noreferrer"
          >
            Anthropic
          </a>
          .
        </p>
      </section>

      {/* ---------- Advanced (collapsed) ---------- */}
      <section className="settings-section">
        <h3>
          <button
            type="button"
            className="summary-card-toggle"
            onClick={() => setAdvancedOpen((v) => !v)}
          >
            <span className="summary-card-caret" aria-hidden>
              {advancedOpen ? "▾" : "▸"}
            </span>
            Advanced
          </button>
        </h3>
        {advancedOpen && (
          <div className="ai-advanced">
            <div className="ai-advanced-warning" role="note">
              <span aria-hidden>⚠️</span> These settings are for technical
              users only. Changing them may cause AI features to stop
              working.
            </div>

            {/* --- Bring your own API key --- */}
            <div className="ai-advanced-block">
              <h4>Bring your own API key (optional)</h4>
              <p className="muted ai-advanced-note">
                If you have your own account with Anthropic, OpenAI, or
                Google Gemini, enter your API key here. Your AI calls
                will be billed directly to that provider instead of
                being included in your CaseThread subscription, and
                your monthly usage limit won't apply.
              </p>
              {keyStatus?.set ? (
                <div className="byok-active">
                  <span className="badge-ready">
                    Key set —{" "}
                    {PROVIDER_INFO[keyStatus.provider]?.label ??
                      keyStatus.provider}
                    {keyStatus.model ? ` (${keyStatus.model})` : ""}
                  </span>
                  {keyStatus.set_at && (
                    <span className="muted byok-set-at">
                      Saved {formatSetAt(keyStatus.set_at)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={removeApiKey}
                    disabled={keySaving}
                    className="byok-remove"
                  >
                    Remove key
                  </button>
                </div>
              ) : (
                <div className="byok-form">
                  <label className="byok-field">
                    <span className="byok-field-label">Provider</span>
                    <select
                      value={keyProvider}
                      onChange={(e) => {
                        const next = e.target.value as ByoProvider;
                        setKeyProvider(next);
                        // A model ID is provider-specific — an
                        // Anthropic model string won't work on Gemini.
                        // Reset both the dropdown and the free-text
                        // custom field so the user re-picks under the
                        // new provider.
                        setModelPick("");
                        setCustomModel("");
                        setKeyError(null);
                      }}
                      disabled={keySaving}
                    >
                      {BYOK_PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {PROVIDER_INFO[p].label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="muted byok-help">
                    {PROVIDER_INFO[keyProvider].help}
                  </p>
                  <label className="byok-field">
                    <span className="byok-field-label">Model</span>
                    <select
                      value={modelPick}
                      onChange={(e) => setModelPick(e.target.value)}
                      disabled={keySaving}
                    >
                      <option value="">Use provider default</option>
                      {PROVIDER_INFO[keyProvider].models.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="byok-field">
                    <span className="byok-field-label">
                      Custom model (optional)
                    </span>
                    <input
                      type="text"
                      value={customModel}
                      onChange={(e) => setCustomModel(e.target.value)}
                      placeholder="e.g. a fine-tune or a newer model ID"
                      autoComplete="off"
                      spellCheck={false}
                      disabled={keySaving}
                    />
                  </label>
                  <label className="byok-field">
                    <span className="byok-field-label">API key</span>
                    <input
                      type="password"
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      placeholder={PROVIDER_INFO[keyProvider].keyPlaceholder}
                      autoComplete="off"
                      spellCheck={false}
                      className="byok-input"
                      disabled={keySaving}
                    />
                  </label>
                  <div className="byok-save-row">
                    <button
                      type="button"
                      onClick={saveApiKey}
                      disabled={keySaving || !keyInput.trim()}
                    >
                      {keySaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
              {keyError && <div className="form-error">{keyError}</div>}
            </div>

            {/* --- AI provider --- */}
            <div className="ai-advanced-block">
              <h4>AI provider</h4>
              <div className="ollama-status">
                {models ? (
                  <>
                    <span
                      className={`ollama-status-dot ${
                        models.ollama_available ? "live" : "down"
                      }`}
                    />
                    {models.ollama_available
                      ? "Ollama running — local models available"
                      : "Ollama not reachable — local models unavailable"}
                    {!models.ollama_available && (
                      <>
                        {" · "}
                        <a
                          href="https://ollama.com/download"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Install Ollama
                        </a>
                      </>
                    )}
                  </>
                ) : (
                  <span className="muted">Loading Ollama status…</span>
                )}
                {modelsError && (
                  <span className="form-error">{modelsError}</span>
                )}
              </div>
              <Row
                label="Provider mode"
                hint="Auto uses the routing table; Local only forces Ollama with a Claude fallback; Cloud only always uses Claude."
              >
                <div className="segmented">
                  {(
                    [
                      ["auto", "Auto (recommended)"],
                      ["local", "Local only"],
                      ["cloud", "Cloud only"],
                    ] as [AiProviderMode, string][]
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      className={settings.ai.provider === v ? "active" : ""}
                      onClick={() => setProvider(v)}
                      disabled={settings.ai.privacy && v !== "local"}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Row>
              <Row
                label="🔒 Privacy mode"
                hint="When enabled, your data never leaves your device. Tasks that can't run locally return an error instead of falling back to the cloud."
              >
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.ai.privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                  />
                  <span>{settings.ai.privacy ? "On" : "Off"}</span>
                </label>
              </Row>
            </div>

            {/* --- Models table --- */}
            <div className="ai-advanced-block">
              <h4>Models</h4>
              <p className="muted ai-advanced-note">
                {totalLocalGb > 0 ? (
                  <>
                    Approximate disk used by pulled local models:{" "}
                    <strong>{totalLocalGb.toFixed(1)} GB</strong>.
                  </>
                ) : (
                  <>No local models pulled yet.</>
                )}
              </p>
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Kind</th>
                    <th>Size</th>
                    <th>Best for</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(models?.local ?? []).map((m) => {
                    const p = pullState[m.name];
                    return (
                      <tr key={m.name}>
                        <td>
                          <ModelLabel name={m.name} kind="local" />
                        </td>
                        <td>
                          <span className="model-kind-badge model-local">
                            Local
                          </span>
                        </td>
                        <td className="muted">~{m.sizeGb} GB</td>
                        <td>{m.bestFor}</td>
                        <td>
                          {p ? (
                            <span className="muted">
                              {p.status}
                              {p.progress > 0 && ` — ${p.progress}%`}
                            </span>
                          ) : m.pulled ? (
                            <span className="badge-ready">Ready</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => pull(m.name)}
                              disabled={!models?.ollama_available}
                            >
                              Pull
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {(models?.cloud ?? []).map((m) => (
                    <tr key={m.name}>
                      <td>
                        <ModelLabel name={m.name} kind="cloud" />
                      </td>
                      <td>
                        <span className="model-kind-badge model-cloud">
                          Cloud
                        </span>
                      </td>
                      <td className="muted">—</td>
                      <td>{m.bestFor}</td>
                      <td>
                        {m.configured ? (
                          <span className="badge-ready">Configured</span>
                        ) : (
                          <span className="form-error">Not configured</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* --- Per-task overrides --- */}
            <div className="ai-advanced-block">
              <h4>Per-task model override</h4>
              <div className="task-overrides">
                {TASK_LABELS.map(({ task, label }) => (
                  <Row key={task} label={label}>
                    <ModelSelect
                      value={settings.ai.overrides[task] ?? "auto"}
                      onChange={(v) => setOverride(task, v)}
                      models={providerModels}
                    />
                  </Row>
                ))}
              </div>
            </div>

            {/* --- API usage cost breakdown --- */}
            <div className="ai-advanced-block">
              <h4>API usage</h4>
              {!usage ? (
                <p className="muted">Loading usage…</p>
              ) : (
                <>
                  <div className="usage-stats">
                    <Stat
                      label="This month (Claude)"
                      value={`$${usage.this_month.cost.toFixed(4)}`}
                      sub={`${usage.this_month.n} calls`}
                    />
                    <Stat
                      label="All time (Claude)"
                      value={`$${usage.all_time.cost.toFixed(4)}`}
                      sub={`${usage.all_time.n} calls`}
                    />
                    <Stat
                      label="Projected month-end"
                      value={`$${usage.month_projection_usd.toFixed(2)}`}
                      sub="scaled from current pace"
                    />
                    <Stat
                      label="Saved by local models"
                      value={`$${usage.savings_usd.toFixed(4)}`}
                      sub="estimated at Claude rates"
                      positive
                    />
                  </div>
                  {usage.breakdown.length > 0 && (
                    <table className="models-table usage-breakdown">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Provider</th>
                          <th>Calls</th>
                          <th>Tokens in / out</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.breakdown.map((r, i) => (
                          <tr key={i}>
                            <td>{r.task_type}</td>
                            <td>
                              <span
                                className={`model-kind-badge model-${
                                  r.provider === "ollama" ? "local" : "cloud"
                                }`}
                              >
                                {r.provider === "ollama" ? "Local" : "Cloud"}
                              </span>
                            </td>
                            <td className="muted">{r.n}</td>
                            <td className="muted">
                              {r.in_tok.toLocaleString()} /{" "}
                              {r.out_tok.toLocaleString()}
                            </td>
                            <td>${r.cost.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>

            {/* --- Setup instructions --- */}
            <div className="ai-advanced-block">
              <h4>
                <button
                  type="button"
                  className="summary-card-toggle"
                  onClick={() => setSetupOpen((v) => !v)}
                >
                  <span className="summary-card-caret" aria-hidden>
                    {setupOpen ? "▾" : "▸"}
                  </span>
                  How to set up local AI
                </button>
              </h4>
              {setupOpen && (
                <ol className="setup-steps">
                  <li>
                    Install Ollama: <code>brew install ollama</code>{" "}
                    (macOS) or{" "}
                    <a
                      href="https://ollama.com/download"
                      target="_blank"
                      rel="noreferrer"
                    >
                      download
                    </a>{" "}
                    for your platform.
                  </li>
                  <li>
                    Start it: <code>ollama serve</code>. Ollama listens on{" "}
                    <code>http://localhost:11434</code>.
                  </li>
                  <li>
                    Run the API Worker in local-dev mode so it can reach
                    Ollama:
                    <pre className="setup-pre">
                      cd workers/api && npx wrangler dev
                    </pre>
                    Then point your frontend at it by setting{" "}
                    <code>VITE_API_BASE=http://127.0.0.1:8787</code> before{" "}
                    <code>npm run dev</code>.
                  </li>
                  <li>
                    Pull the models you want using the{" "}
                    <strong>Pull</strong> buttons above (or{" "}
                    <code>ollama pull {"<name>"}</code>).
                  </li>
                  <li>
                    Set <strong>Provider mode</strong> to Auto or Local only.
                  </li>
                  <li>
                    <em>Deployed production note:</em> the Worker deployed
                    to Cloudflare can't reach your laptop, so local AI only
                    functions in <code>wrangler dev</code>. In production
                    everything routes to Claude.
                  </li>
                </ol>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Usage meter — colour-coded progress bar, reset date, BYO badge.
// ---------------------------------------------------------------------------

function UsageMeterPanel({ meter }: { meter: UsageMeter | null }) {
  if (!meter) {
    return <p className="muted">Loading usage…</p>;
  }

  if (meter.byo_key_active) {
    const provider = meter.byo_provider
      ? (PROVIDER_INFO[meter.byo_provider]?.label ?? meter.byo_provider)
      : "Anthropic";
    const modelSuffix = meter.byo_model ? ` (${meter.byo_model})` : "";
    return (
      <div className="usage-meter-byok">
        <span className="badge-ready">
          Using your own {provider} key{modelSuffix}
        </span>
        <p className="muted usage-meter-byok-note">
          No CaseThread usage limits apply. Your AI calls are billed
          directly to your {provider} account. Disable by removing your
          API key in Advanced.
        </p>
      </div>
    );
  }

  // Non-BYOK users always get a numeric limit from the worker, but
  // the type is `number | null` so we guard here rather than trust
  // the invariant.
  if (meter.limit === null) {
    return <p className="muted">Loading usage…</p>;
  }
  const limit = meter.limit;
  const ratio = Math.min(1, meter.ai_calls / limit);
  const pct = Math.round(ratio * 100);
  const level: "ok" | "warn" | "critical" =
    ratio >= 0.9 ? "critical" : ratio >= 0.5 ? "warn" : "ok";

  return (
    <div className="usage-meter">
      <div className="usage-meter-row">
        <div
          className={`usage-meter-bar usage-meter-${level}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={meter.ai_calls}
          aria-label={`${meter.ai_calls} of ${limit} AI requests used`}
        >
          <div
            className="usage-meter-fill"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="usage-meter-count">
          {meter.ai_calls.toLocaleString()} / {limit.toLocaleString()}
        </div>
      </div>
      {meter.auditor_calls ? (
        <div className="muted usage-meter-breakdown">
          {(meter.generation_calls ?? meter.ai_calls - meter.auditor_calls).toLocaleString()}{" "}
          generation + {meter.auditor_calls.toLocaleString()} quality{" "}
          {meter.auditor_calls === 1 ? "check" : "checks"}
        </div>
      ) : null}
      <div className="muted usage-meter-reset">
        Resets {formatResetDate(meter.reset_at)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Reduce any stored locale (en, en-US, en-GB) to the 7-option dropdown's
// base code. Anything we don't recognise defaults back to English so the
// dropdown stays in a valid state.
function normaliseLanguage(code: string): string {
  const base = (code || "en").toLowerCase().split("-")[0] ?? "en";
  const known = DRAFT_LANGUAGES.map((l) => l.value);
  return known.includes(base) ? base : "en";
}

function formatResetDate(iso: string): string {
  return formatDate(iso, "long") || iso;
}

function formatSetAt(iso: string): string {
  return formatDate(iso, "short") || iso;
}

function ModelSelect({
  value,
  onChange,
  models,
}: {
  value: string;
  onChange: (v: string) => void;
  models: Array<{
    value: string;
    label: string;
    kind: "local" | "cloud";
    disabled?: boolean;
  }>;
}) {
  const local = models.filter((m) => m.kind === "local" && m.value !== "auto");
  const cloud = models.filter((m) => m.kind === "cloud" && m.value !== "auto");
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="model-select"
    >
      <option value="auto">Auto (routing table default)</option>
      {local.length > 0 && (
        <optgroup label="🏠 Local models">
          {local.map((m) => (
            <option key={m.value} value={m.value} disabled={m.disabled}>
              🏠 {m.label} — Local{m.disabled ? " (not pulled)" : ""}
            </option>
          ))}
        </optgroup>
      )}
      {cloud.length > 0 && (
        <optgroup label="☁️ Cloud models">
          {cloud.map((m) => (
            <option key={m.value} value={m.value}>
              ☁️ {m.label} — Cloud
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function Stat({
  label,
  value,
  sub,
  positive,
}: {
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
}) {
  return (
    <div className={`usage-stat${positive ? " positive" : ""}`}>
      <div className="usage-stat-label">{label}</div>
      <div className="usage-stat-value">{value}</div>
      {sub && <div className="usage-stat-sub">{sub}</div>}
    </div>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="settings-row">
      <div className="settings-row-label">
        <div>{label}</div>
        {hint && <div className="settings-row-hint">{hint}</div>}
      </div>
      <div className="settings-row-control">{children}</div>
    </div>
  );
}
